import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import AdminCard from './AdminCard';
import AdminCardActions from './AdminCardActions';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useAdmin } from './AdminHooks';
import { Utensils, Link2, Pencil, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';

const ModernTablesTab = () => {
  const {
    events, tables, codes,
    fetchTables, fetchCodes, createTablesBulk, createTable, updateTable, deleteTable,
    tablesEventId, setTablesEventId,
  } = useAdmin();

  const [bulkCount, setBulkCount] = useState('');
  const [baseName, setBaseName] = useState('');
  const [singleName, setSingleName] = useState('');
  const [singleNumber, setSingleNumber] = useState('');
  const [singleCodeId, setSingleCodeId] = useState('');
  const [editingId, setEditingId] = useState('');
  const [editName, setEditName] = useState('');
  const [editNumber, setEditNumber] = useState('');
  const [editSlug, setEditSlug] = useState('');
  const [editCodeId, setEditCodeId] = useState('');
  const [editOpen, setEditOpen] = useState(false);

  useEffect(() => {
    if (tablesEventId) {
      fetchTables(tablesEventId);
    }
  }, [tablesEventId]);
  useEffect(() => {
    fetchCodes();
  }, []);

  const onSelectEvent = (val) => {
    setTablesEventId(val);
  };

  const publicUrl = (slug) => `${window.location.origin}/t/${slug}`;
  const codeById = useMemo(
    () => Object.fromEntries((codes || []).map((code) => [code.id, code.code])),
    [codes]
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Tables</h2>
          <p className="text-slate-400">
            {(events || []).length} événement(s) • {tables.length} table(s)
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={tablesEventId} onValueChange={onSelectEvent}>
            <SelectTrigger className="w-[220px] bg-slate-900 border-slate-700 text-white">
              <SelectValue placeholder="Sélectionner l'événement" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              {(events || []).map(e => (
                <SelectItem key={e.id} value={e.id} className="text-white">
                  Vol.{e.volume} • {e.theme}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Section création multiple masquée */}

      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white">Créer une table</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-4">
            <Input
              placeholder="Nom"
              value={singleName}
              onChange={(e) => setSingleName(e.target.value)}
              className="text-white bg-slate-900 border-slate-700 placeholder-slate-400"
            />
            <Input
              placeholder="Numéro"
              value={singleNumber}
              onChange={(e) => setSingleNumber(e.target.value)}
              className="w-32 text-white bg-slate-900 border-slate-700 placeholder-slate-400"
            />
            <Select value={singleCodeId} onValueChange={(v) => setSingleCodeId(v === 'none' ? '' : v)}>
              <SelectTrigger className="w-full text-white bg-slate-900 border-slate-700">
                <SelectValue placeholder="Code (optionnel)" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                <SelectItem value="none" className="text-white">Aucun</SelectItem>
                {(codes || []).map(c => (
                  <SelectItem key={c.id} value={c.id} className="text-white">
                    {c.code}{typeof c.percent === 'number' ? ` • ${c.percent}%` : ''}{typeof c.flat === 'number' ? ` • ${new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' }).format(c.flat)}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              onClick={() => {
                if (!tablesEventId || !singleName) return;
                const num = Number.isFinite(parseInt(singleNumber)) ? parseInt(singleNumber) : null;
                createTable(tablesEventId, singleName, num, null, singleCodeId || null);
                setSingleName('');
                setSingleNumber('');
                setSingleCodeId('');
              }}
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
            >
              Ajouter
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white">Tables</CardTitle>
        </CardHeader>
        <CardContent>
          {tables.length === 0 ? (
            <p className="text-slate-400">Aucune table pour cet événement</p>
          ) : (
            <div className="grid gap-3 sm:gap-4 grid-cols-[repeat(auto-fit,minmax(280px,1fr))]">
              {tables.map(t => (
                <AdminCard key={t.id}>
                  <CardHeader className="gap-3 pb-2">
                    <div className="flex gap-3 items-start">
                      <div className="flex justify-center items-center mt-0.5 w-8 h-8 rounded-lg border bg-blue-500/10 border-blue-500/20 shrink-0 sm:w-9 sm:h-9">
                        <Utensils size={15} className="text-blue-400 sm:w-4 sm:h-4" />
                      </div>
                      <div className="space-y-2 min-w-0">
                        <CardTitle className="text-base font-semibold leading-tight text-white whitespace-normal break-words">
                          {t.name}
                        </CardTitle>
                        <div className="flex flex-wrap gap-1.5 sm:gap-2">
                          {t.number ? (
                            <Badge className="bg-slate-700 text-slate-200 border-slate-600">#{t.number}</Badge>
                          ) : null}
                          {t.codeId ? (
                            <Badge className="bg-slate-700 text-slate-200 border-slate-600 whitespace-normal break-all">
                              {codeById[t.codeId] || 'code'}
                            </Badge>
                          ) : null}
                          <Badge className="bg-slate-700 text-slate-200 border-slate-600">{t.id.slice(0, 4)}</Badge>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="space-y-2 sm:grid sm:grid-cols-2 sm:gap-2 sm:space-y-0">
                      {t.number ? (
                        <div className="flex gap-3 justify-between items-start p-2.5 rounded-lg border bg-slate-950/40 border-slate-800 sm:block sm:p-3">
                          <p className="text-[11px] font-medium tracking-wide uppercase text-slate-500 sm:text-xs">Numero</p>
                          <p className="text-sm font-semibold text-right text-white break-words sm:mt-1 sm:text-left">#{t.number}</p>
                        </div>
                      ) : null}
                      {t.codeId ? (
                        <div className="flex gap-3 justify-between items-start p-2.5 rounded-lg border bg-slate-950/40 border-slate-800 sm:block sm:p-3">
                          <p className="text-[11px] font-medium tracking-wide uppercase text-slate-500 sm:text-xs">Code</p>
                          <p className="text-sm font-semibold text-right text-white break-all sm:mt-1 sm:text-left">{codeById[t.codeId] || 'code'}</p>
                        </div>
                      ) : null}
                      <div className="flex gap-3 justify-between items-start p-2.5 rounded-lg border bg-slate-950/40 border-slate-800 sm:block sm:p-3 sm:col-span-2">
                        <p className="text-[11px] font-medium tracking-wide uppercase text-slate-500 sm:text-xs">Reference</p>
                        <p className="font-mono text-sm text-right text-slate-200 break-all sm:mt-1 sm:text-left">{t.id.slice(0, 4)}</p>
                      </div>
                    </div>
                  </CardContent>
                  <AdminCardActions>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        navigator.clipboard.writeText(publicUrl(t.slug)).catch(() => {});
                      }}
                      className="flex-1 text-slate-300 hover:text-white hover:bg-slate-700 sm:flex-none"
                      title="Copier le lien"
                      aria-label="Copier le lien"
                    >
                      <Link2 size={14} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setEditingId(t.id);
                        setEditName(t.name);
                        setEditNumber(String(t.number || ''));
                        setEditSlug(String(t.slug || ''));
                        setEditCodeId(String(t.codeId || ''));
                        setEditOpen(true);
                      }}
                      className="flex-1 text-slate-300 hover:text-white hover:bg-slate-700 sm:flex-none"
                      title="Modifier"
                      aria-label="Modifier"
                    >
                      <Pencil size={14} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        if (!tablesEventId) return;
                        if (!window.confirm('Supprimer cette table ?')) return;
                        deleteTable(tablesEventId, t.id);
                      }}
                      className="flex-1 text-slate-300 hover:text-red-400 hover:bg-red-500/10 sm:flex-none"
                      title="Supprimer"
                      aria-label="Supprimer"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </AdminCardActions>
                </AdminCard>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Modifier la table</DialogTitle>
            <DialogDescription className="text-slate-300">
              Modifiez le nom, le numéro et le lien public (slug).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input
                placeholder="Nom"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="text-white bg-slate-900 border-slate-700 placeholder-slate-400"
              />
              <Input
                placeholder="Numéro"
                value={editNumber}
                onChange={(e) => setEditNumber(e.target.value)}
                className="w-28 text-white bg-slate-900 border-slate-700 placeholder-slate-400"
              />
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Slug du lien public (unique)"
                value={editSlug}
                onChange={(e) => setEditSlug(e.target.value)}
                className="text-white bg-slate-900 border-slate-700 placeholder-slate-400"
              />
            </div>
            <div className="flex gap-2">
              <Select value={editCodeId} onValueChange={(v) => setEditCodeId(v === 'none' ? '' : v)}>
                <SelectTrigger className="w-full text-white bg-slate-900 border-slate-700">
                  <SelectValue placeholder="Code (optionnel)" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="none" className="text-white">Aucun</SelectItem>
                  {(codes || []).map(c => (
                    <SelectItem key={c.id} value={c.id} className="text-white">
                      {c.code}{typeof c.percent === 'number' ? ` • ${c.percent}%` : ''}{typeof c.flat === 'number' ? ` • ${new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' }).format(c.flat)}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  if (!tablesEventId || !editingId) return;
                  if (!editName) return;
                  const num = Number.isFinite(parseInt(editNumber)) ? parseInt(editNumber) : null;
                  const slug = (editSlug || '').trim();
                  if (!slug) { alert('Le slug ne peut pas être vide'); return; }
                  updateTable(tablesEventId, editingId, { name: editName, number: num, slug, codeId: editCodeId });
                  setEditOpen(false);
                  setEditingId('');
                }}
                className="text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              >
                Enregistrer
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setEditOpen(false);
                  setEditingId('');
                  setEditCodeId('');
                }}
                className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
              >
                Annuler
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ModernTablesTab;
